import { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Divider, IconButton, Modal, Typography } from "@mui/material";

import { User } from "../../../../types/types";

import { UserRoleEnum } from "../../../User/enums";

import { Bucket } from "../../enum/jobEnums";
import { CloseButtonIcon } from "../../assets/icons/components-icons";
import { useReviewCandidate } from "../../api/mutations/useReviewCandidate";
import { AddProfessionalReviewCandidate, AddTalentReviewCandidate } from "../../types/addReviewCandidate";
import { formatProfessionalReviewPayload, formatTalentReviewPayload } from "../../api/formatters/candidate.formatter";

import TalentReportSteps from "./TalentReportSteps";
import ProfessionalReportSteps from "./ProfessionalReportSteps";

export const AddCandidateReviewModal = ({
  bucket,
  open,
  onClose,
  handleAddCandidateReview,
  candidate,
}: {
  bucket: Bucket;
  open: boolean;
  onClose: () => void;
  handleAddCandidateReview: (data: AddTalentReviewCandidate | AddProfessionalReviewCandidate) => void;
  candidate: User;
}) => {
  const { t } = useTranslation("goHire", { keyPrefix: "components.reviewCandidateModal" });

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={styles.container}>
        <Box sx={[styles.header, styles.containerPadding]}>
          {candidate.role === UserRoleEnum.PLAYER ? (
            <Typography sx={styles.headerTitle}>{t("addReviewTalents")}</Typography>
          ) : (
            <Typography sx={styles.headerTitle}>{t("addReviewProfessionals")}</Typography>
          )}
          <IconButton size="small" onClick={onClose}>
            <CloseButtonIcon />
          </IconButton>
        </Box>
        <Divider flexItem orientation="horizontal" />
        {candidate.role === UserRoleEnum.PLAYER && (
          <TalentReportSteps bucket={bucket} onClose={onClose} handleAddCandidateReview={handleAddCandidateReview} />
        )}
        {candidate.role !== UserRoleEnum.PLAYER && (
          <ProfessionalReportSteps
            bucket={bucket}
            onClose={onClose}
            handleAddCandidateReview={handleAddCandidateReview}
          />
        )}
      </Box>
    </Modal>
  );
};

const styles = {
  container: {
    width: "900px",
    height: "fit-content",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    borderRadius: "12px",
    border: "1px solid #EAECF0",
    background: "#FFF",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    boxShadow:
      "0 20px 24px -4px var(--Colors-Effects-Shadows-shadow-xl_01, rgba(16, 24, 40, 0.08)), 0 8px 8px -4px var(--Colors-Effects-Shadows-shadow-xl_02, rgba(16, 24, 40, 0.03))",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    pt: "24px",
  },
  containerPadding: {
    px: "24px",
  },
  headerTitle: {
    fontFamily: "Montserrat",
    color: "var(--colors-text-text-primary-900, #101828)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    pb: "24px",
  },
};

export const useAddCandidateReviewModal = (user: User, bucket: Bucket) => {
  const [open, setOpen] = useState(false);
  const { jobId } = useParams<{ jobId: string }>();
  const { mutate: reviewCandidate } = useReviewCandidate();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleAddCandidateReview = (data: AddTalentReviewCandidate | AddProfessionalReviewCandidate) => {
    if (!jobId) return;

    const payload =
      user.role === UserRoleEnum.PLAYER
        ? formatTalentReviewPayload(data as AddTalentReviewCandidate, bucket)
        : formatProfessionalReviewPayload(data as AddProfessionalReviewCandidate, bucket);

    reviewCandidate(
      { jobId, candidateId: user.id, payload },
      {
        onSuccess: () => {
          handleClose();
        },
      }
    );
  };

  return {
    openAddCandidateReviewModal: open,
    handleOpenAddCandidateReviewModal: handleOpen,
    handleCloseAddCandidateReviewModal: handleClose,
    handleAddCandidateReview,
  };
};
